import type { RowSelectionState } from '@tanstack/react-table'
import { Trash2Icon } from 'lucide-react'
import { type RefObject, useEffect } from 'react'

import { Button, DataTable } from '@/components/ui'
import type { ColumnDef, Table } from '@/components/ui/table/types'
import {
  DeleteStatementsDialog,
  ProcessButton,
  type Statement,
  useLoadingStatements,
  useStatements,
} from '@/modules/projects'
import {
  isBankishStatement,
  isStatementToStart,
} from '@/modules/projects/status'
import { useDialogActions, useSubmittedDialog } from '@/stores/dialog'
import { cn } from '@/utils'
import { pagesKeptUnread } from '../../../trial'
import { StatementContentsPanel } from './StatementContentsPanel'

const canExpandStatement = (statement: Statement) =>
  (statement.segments?.length ?? 0) > 0 ||
  (statement.excludedRanges?.length ?? 0) > 0 ||
  pagesKeptUnread(statement) > 0

interface StatementsDataTableProps {
  onTableReady?: (table: Table<Statement>) => void
  onSelectionChange?: (selection: RowSelectionState) => void
  columns: ColumnDef<Statement, any>[]
  table: RefObject<Table<Statement> | null>
  height?: string
  // Row-click expansion with the per-file contents panel. Only the Files tab turns this on; the
  // compact wizard table has no Contents column, so no chevron affordance.
  expandable?: boolean
}

export const StatementsDataTable = ({
  columns,
  table,
  onTableReady,
  onSelectionChange,
  height = 'h-auto',
  expandable = false,
}: StatementsDataTableProps) => {
  const { openDialog } = useDialogActions()
  const submittedDialog = useSubmittedDialog()

  // Checks-only uploads have their own view; here we render only bank-style statements.
  // Reconcile review now lives on the Insights Reconciliation tab, not this table.
  const statements = useStatements().filter(isBankishStatement)
  const isLoadingStatements = useLoadingStatements()

  const getStatementsFromSelection = (
    selection: RowSelectionState,
  ): Statement[] => statements.filter((statement) => selection[statement.id])

  useEffect(() => {
    if (submittedDialog && table.current) {
      table.current.resetRowSelection()
    }
  }, [submittedDialog, table])

  return (
    <div
      className={cn(
        'flex-1 flex flex-col rounded-lg border bg-white overflow-auto',
      )}
    >
      <DataTable
        columns={columns}
        data={statements}
        height={height}
        onTableReady={onTableReady}
        onSelectionChange={onSelectionChange}
        emptyMessage="No statements found"
        isLoading={isLoadingStatements}
        getRowCanExpand={
          expandable ? (row) => canExpandStatement(row.original) : undefined
        }
        renderSubComponent={
          expandable
            ? (row) => <StatementContentsPanel statement={row.original} />
            : undefined
        }
        scrollbarClassName="overflow-auto scrollbar-thin scrollbar-thumb-neutral-300 hover:scrollbar-thumb-neutral-400 scrollbar-track-transparent"
        bulkActions={(selection) => {
          const statements = getStatementsFromSelection(selection)

          return (
            <>
              <ProcessButton
                table={table.current}
                statementsToProcess={statements}
                size="sm"
                theme="outline"
                className="h-8 gap-1.5 text-xs font-medium"
                validationBehaviour="disable-only"
                disabled={statements.some(
                  (statement) => !isStatementToStart(statement),
                )}
              >
                Process selected
              </ProcessButton>
              <Button
                theme="outline"
                size="sm"
                onClick={() =>
                  openDialog(DeleteStatementsDialog.id, statements)
                }
                className="h-8 gap-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2Icon className="size-3.5" />
                Delete
              </Button>
            </>
          )
        }}
      />
    </div>
  )
}
